import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PLATFORM = "Fortunate Operations";
const EMAIL = "laz@fortunateops.com";
const SUPPORT = "+1 954-516-2206";
const EFFECTIVE = { en: "Effective July 28, 2026", es: "Vigente a partir del 28 de julio de 2026" };

function Shell({ title, lang, setLang, children, testid }) {
  return (
    <div className="min-h-screen bg-[#17324D] text-slate-100" data-testid={testid}>
      <div className="mx-auto w-full max-w-3xl px-6 py-10">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#d9b978]" data-testid="legal-home-link">
            <ArrowLeft className="h-3.5 w-3.5" /> {PLATFORM}
          </Link>
          <div className="flex gap-1">
            {["en", "es"].map((l) => (
              <button key={l} onClick={() => setLang(l)}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold ${lang === l ? "bg-[#B28A3D] text-white" : "bg-white/10 text-slate-300"}`}
                data-testid={`legal-lang-${l}`}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <h1 className="mt-6 font-heading text-3xl font-extrabold text-white" data-testid="legal-title">{title}</h1>
        <div className="mt-1 text-[12px] text-[#d9b978]" data-testid="legal-effective">{EFFECTIVE[lang]}</div>
        <div className="mt-6 space-y-5 text-sm leading-relaxed text-slate-300">{children}</div>
        <div className="mt-10 flex gap-4 border-t border-white/10 pt-4 text-[11px] text-slate-400" data-testid="legal-footer">
          <Link to="/privacy" className="hover:text-white">{lang === "es" ? "Privacidad" : "Privacy"}</Link>
          <Link to="/terms" className="hover:text-white">{lang === "es" ? "Términos" : "Terms"}</Link>
          <Link to="/contact" className="hover:text-white">{lang === "es" ? "Contacto" : "Contact"}</Link>
        </div>
      </div>
    </div>
  );
}

const S = ({ h, children }) => (
  <div><h2 className="text-base font-semibold text-white">{h}</h2><div className="mt-1 space-y-1">{children}</div></div>
);
const UL = ({ items }) => (
  <ul className="ml-4 list-disc space-y-0.5">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
);

/* ------------------------------------------------------------------ CONTACT */
export function Contact() {
  const [lang, setLang] = useState("en");
  const en = lang === "en";
  return (
    <Shell title={en ? "Contact & Messaging Support" : "Contacto y Soporte de Mensajería"} lang={lang} setLang={setLang} testid="contact-page">
      <div className="rounded-lg bg-white/5 p-4 text-sm">
        <div className="font-semibold text-white">{PLATFORM}</div>
        <div className="mt-1"><span className="text-slate-400">{en ? "Email" : "Correo"}:</span> <a href={`mailto:${EMAIL}`} className="underline" data-testid="contact-email">{EMAIL}</a></div>
        <div className="mt-1"><span className="text-slate-400">{en ? "Messaging support" : "Soporte de mensajería"}:</span> <span data-testid="contact-phone">{SUPPORT}</span></div>
      </div>
      <S h={en ? "SMS messaging support" : "Soporte de mensajería SMS"}>
        <UL items={en ? [
          "Reply STOP to any text message to unsubscribe from SMS.",
          "Reply HELP for messaging assistance.",
          "Message and data rates may apply.",
          "Message frequency varies.",
          "SMS consent is not a condition of purchasing any product or service.",
          "Insurance emergencies should not be handled through SMS.",
        ] : [
          "Responda STOP a cualquier mensaje de texto para cancelar la suscripción a SMS.",
          "Responda HELP para obtener asistencia de mensajería.",
          "Pueden aplicarse tarifas de mensajes y datos.",
          "La frecuencia de los mensajes varía.",
          "El consentimiento de SMS no es una condición para comprar ningún producto o servicio.",
          "Las emergencias de seguros no deben gestionarse a través de SMS.",
        ]} />
      </S>
      <S h={en ? "Stopping calls" : "Detener las llamadas"}>
        <p>{en
          ? `To stop future automated or AI-assisted calls, tell the caller that you no longer wish to receive calls, or contact ${PLATFORM} using the email or messaging-support number above.`
          : `Para detener futuras llamadas automatizadas o asistidas por IA, dígale a la persona que llama que ya no desea recibir llamadas, o comuníquese con ${PLATFORM} usando el correo o el número de soporte de mensajería anteriores.`}</p>
      </S>
      <p className="text-[12px]">
        <Link to="/privacy" className="underline">{en ? "Privacy Policy" : "Política de Privacidad"}</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="underline">{en ? "Terms of Service" : "Términos del Servicio"}</Link>
      </p>
    </Shell>
  );
}

/* ------------------------------------------------------------------ PRIVACY */
export function PrivacyPolicy() {
  const [lang, setLang] = useState("en");
  const en = lang === "en";
  const MOBILE_EN = "Mobile information, including phone numbers and SMS consent records, will not be sold, rented, or shared with third parties or affiliates for their marketing or promotional purposes. Mobile information may be shared only with service providers that help Fortunate Operations and the participating agency operate the messaging service, and only as necessary to provide that service. Text messaging originator opt-in data and consent will not be shared with third parties for marketing or promotional purposes.";
  const MOBILE_ES = "La información móvil, incluidos los números de teléfono y los registros de consentimiento de SMS, no se venderá, alquilará ni compartirá con terceros o afiliados para sus fines de marketing o promoción. La información móvil solo podrá compartirse con proveedores de servicios que ayudan a Fortunate Operations y a la agencia participante a operar el servicio de mensajería, y únicamente en la medida necesaria para prestar ese servicio. Los datos de aceptación (opt-in) del originador de los mensajes de texto y el consentimiento no se compartirán con terceros para fines de marketing o promoción.";
  const CALL_EN = "Calling permission, telephone information, call outcomes, and call-consent records will not be sold or shared for unrelated third-party marketing.";
  const CALL_ES = "El permiso de llamadas, la información telefónica, los resultados de las llamadas y los registros de consentimiento de llamadas no se venderán ni compartirán para marketing de terceros no relacionado.";
  return (
    <Shell title={en ? "Privacy Policy" : "Política de Privacidad"} lang={lang} setLang={setLang} testid="privacy-page">
      <S h={en ? "A. Information we collect" : "A. Información que recopilamos"}>
        <p>{en ? "Depending on your request and interactions, we may collect:" : "Según su solicitud e interacciones, podemos recopilar:"}</p>
        <UL items={en ? [
          "First and last name", "Phone number", "Email address", "State", "Date of birth or age range",
          "Coverage interest", "Preferred language", "Campaign and advertisement attribution", "IP address",
          "Browser/device (user-agent) information", "Consent choices, consent text version, and consent timestamp",
          "Communications and opt-out history", "SMS delivery and status information",
          "Call date, time, status, and duration", "Appointment outcome", "Provider transaction identifiers",
          "Call transcript or recording only when that feature is enabled and lawfully disclosed",
        ] : [
          "Nombre y apellido", "Número de teléfono", "Correo electrónico", "Estado", "Fecha de nacimiento o rango de edad",
          "Interés de cobertura", "Idioma preferido", "Atribución de campaña y anuncio", "Dirección IP",
          "Información del navegador/dispositivo (user-agent)", "Opciones de consentimiento, versión del texto de consentimiento y marca de tiempo",
          "Historial de comunicaciones y cancelaciones", "Información de entrega y estado de SMS",
          "Fecha, hora, estado y duración de la llamada", "Resultado de la cita", "Identificadores de transacción del proveedor",
          "Transcripción o grabación de la llamada únicamente cuando esa función esté habilitada y divulgada legalmente",
        ]} />
      </S>
      <S h={en ? "B. How we use information" : "B. Cómo usamos la información"}>
        <UL items={en ? [
          "Respond to an insurance-information request", "Connect you with the participating agency or a licensed insurance professional",
          "Acknowledge your request", "Ask qualification questions", "Schedule appointments", "Provide reminders",
          "Provide status updates", "Conduct follow-up", "Operate and secure the platform", "Prevent fraud and abuse",
          "Maintain consent and audit records", "Honor opt-out and Do Not Call requests",
        ] : [
          "Responder a una solicitud de información de seguros", "Conectarlo con la agencia participante o un profesional de seguros con licencia",
          "Confirmar su solicitud", "Hacer preguntas de calificación", "Programar citas", "Enviar recordatorios",
          "Proporcionar actualizaciones de estado", "Realizar seguimiento", "Operar y asegurar la plataforma", "Prevenir fraude y abuso",
          "Mantener registros de consentimiento y auditoría", "Respetar solicitudes de cancelación y de No Llamar",
        ]} />
      </S>
      <S h={en ? "C. Service providers" : "C. Proveedores de servicios"}>
        <p>{en
          ? `${PLATFORM} and the participating agency may use authorized service providers to operate: SMS messaging; live calling; automated dialing; artificial, prerecorded, or AI-generated voice calling; appointment scheduling; email delivery; and hosting and security. Providers may change over time.`
          : `${PLATFORM} y la agencia participante pueden usar proveedores de servicios autorizados para operar: mensajería SMS; llamadas en vivo; marcación automatizada; llamadas de voz artificial, pregrabada o generada por IA; programación de citas; entrega de correo electrónico; y alojamiento y seguridad. Los proveedores pueden cambiar con el tiempo.`}</p>
      </S>
      <S h={en ? "D. Mobile information" : "D. Información móvil"}>
        <p>{en ? MOBILE_EN : MOBILE_ES}</p>
      </S>
      <S h={en ? "E. Calling information" : "E. Información de llamadas"}>
        <p>{en ? CALL_EN : CALL_ES}</p>
      </S>
      <S h={en ? "F. Your choices" : "F. Sus opciones"}>
        <UL items={en ? [
          "Message frequency varies according to your request and interactions.",
          "Message and data rates may apply.",
          "Reply STOP to unsubscribe from SMS.",
          "Reply HELP for assistance.",
          "SMS consent is not a condition of purchasing any product or service.",
          "Calling consent is not a condition of purchasing any product or service.",
          "Opting out of SMS or calls does not prevent you from submitting an insurance-information request.",
          "You may revoke calling permission at any time.",
          "Do Not Call and opt-out requests will be preserved and honored.",
          `Support is available at ${EMAIL} and ${SUPPORT}.`,
        ] : [
          "La frecuencia de los mensajes varía según su solicitud e interacciones.",
          "Pueden aplicarse tarifas de mensajes y datos.",
          "Responda STOP para cancelar la suscripción a SMS.",
          "Responda HELP para obtener asistencia.",
          "El consentimiento de SMS no es una condición para comprar ningún producto o servicio.",
          "El consentimiento de llamadas no es una condición para comprar ningún producto o servicio.",
          "Cancelar SMS o llamadas no le impide enviar una solicitud de información de seguros.",
          "Puede revocar el permiso de llamadas en cualquier momento.",
          "Las solicitudes de No Llamar y de cancelación se conservarán y respetarán.",
          `El soporte está disponible en ${EMAIL} y ${SUPPORT}.`,
        ]} />
        <p className="mt-2">{en
          ? "Information may be shared with the participating agency, licensed professionals, and operational service providers as necessary to respond to your request."
          : "La información puede compartirse con la agencia participante, profesionales con licencia y proveedores de servicios operativos según sea necesario para responder a su solicitud."}</p>
      </S>
    </Shell>
  );
}

/* ------------------------------------------------------------------ TERMS */
export function Terms() {
  const [lang, setLang] = useState("en");
  const en = lang === "en";
  return (
    <Shell title={en ? "Terms of Service" : "Términos del Servicio"} lang={lang} setLang={setLang} testid="terms-page">
      <S h={en ? "General" : "General"}>
        <UL items={en ? [
          "Submitting a request does not create an insurance policy.",
          "Coverage is not guaranteed.",
          "Approval is not guaranteed.",
          "Pricing and savings are not guaranteed.",
          `${PLATFORM} is not an insurance carrier.`,
          `${PLATFORM} is not a government agency.`,
          "Licensed insurance professionals and participating agencies remain responsible for insurance recommendations and transactions.",
        ] : [
          "Enviar una solicitud no crea una póliza de seguro.",
          "La cobertura no está garantizada.",
          "La aprobación no está garantizada.",
          "Los precios y ahorros no están garantizados.",
          `${PLATFORM} no es una compañía de seguros.`,
          `${PLATFORM} no es una agencia gubernamental.`,
          "Los profesionales de seguros con licencia y las agencias participantes siguen siendo responsables de las recomendaciones y transacciones de seguros.",
        ]} />
      </S>
      <S h={en ? "SMS Messaging Terms" : "Términos de Mensajería SMS"}>
        <UL items={en ? [
          "Program name: Fortunate Operations Final Expense Information.",
          "Consumers receive messages only after providing affirmative SMS consent.",
          "Messages may include request acknowledgment, requested information, qualification follow-up, appointment scheduling, reminders, and status updates related to your insurance inquiry.",
          "Message frequency varies.",
          "Message and data rates may apply.",
          "Reply STOP to unsubscribe.",
          "Reply HELP for assistance.",
          `Support is available at ${EMAIL} and ${SUPPORT}.`,
          "Consent to receive SMS is not a condition of purchase.",
          "Carriers are not liable for delayed or undelivered messages.",
          "You must provide a valid mobile number that you control.",
          "SMS consent applies only to the specific insurance-information request.",
          "Mobile opt-in information will not be sold or shared for third-party marketing.",
        ] : [
          "Nombre del programa: Fortunate Operations Final Expense Information.",
          "Los consumidores reciben mensajes solo después de otorgar consentimiento afirmativo de SMS.",
          "Los mensajes pueden incluir confirmación de la solicitud, información solicitada, seguimiento de calificación, programación de citas, recordatorios y actualizaciones de estado relacionados con su consulta de seguros.",
          "La frecuencia de los mensajes varía.",
          "Pueden aplicarse tarifas de mensajes y datos.",
          "Responda STOP para cancelar la suscripción.",
          "Responda HELP para obtener asistencia.",
          `El soporte está disponible en ${EMAIL} y ${SUPPORT}.`,
          "El consentimiento para recibir SMS no es una condición de compra.",
          "Los operadores no son responsables de mensajes retrasados o no entregados.",
          "Debe proporcionar un número de móvil válido que usted controle.",
          "El consentimiento de SMS se aplica solo a la solicitud de información de seguros específica.",
          "La información de aceptación móvil no se venderá ni compartirá para marketing de terceros.",
        ]} />
        <p className="mt-1"><Link to="/privacy" className="underline">{en ? "Privacy Policy" : "Política de Privacidad"}</Link></p>
      </S>
      <S h={en ? "Automated and AI-Assisted Voice Calls" : "Llamadas de Voz Automatizadas y Asistidas por IA"}>
        <UL items={en ? [
          "Calls relate only to your submitted insurance-information request.",
          "Calls may be placed by the participating agency, licensed insurance professionals, or authorized technology and service providers.",
          "Calls may use automated dialing technology or an artificial, prerecorded, or AI-generated voice.",
          "Calls may include inquiry acknowledgment, qualification questions, appointment scheduling, reminders, status updates, and follow-up.",
          "Call frequency varies.",
          "Calling consent is not a condition of purchase.",
          "Calling permission may be revoked at any time.",
          "Do Not Call requests must be honored.",
          "No coverage, approval, pricing, savings, appointment, or sales outcome is guaranteed.",
          "You must provide a valid telephone number that you control.",
          "Call-consent information will not be sold or shared for unrelated third-party marketing.",
        ] : [
          "Las llamadas se relacionan únicamente con la solicitud de información de seguros que envió.",
          "Las llamadas pueden ser realizadas por la agencia participante, profesionales de seguros con licencia o proveedores de tecnología y servicios autorizados.",
          "Las llamadas pueden usar tecnología de marcación automatizada o una voz artificial, pregrabada o generada por IA.",
          "Las llamadas pueden incluir confirmación de la consulta, preguntas de calificación, programación de citas, recordatorios, actualizaciones de estado y seguimiento.",
          "La frecuencia de las llamadas varía.",
          "El consentimiento de llamadas no es una condición de compra.",
          "El permiso de llamadas puede revocarse en cualquier momento.",
          "Las solicitudes de No Llamar deben respetarse.",
          "No se garantiza ninguna cobertura, aprobación, precio, ahorro, cita o resultado de venta.",
          "Debe proporcionar un número de teléfono válido que usted controle.",
          "La información de consentimiento de llamadas no se venderá ni compartirá para marketing de terceros no relacionado.",
        ]} />
      </S>
    </Shell>
  );
}
